<template>
    <div
        class="workspace_chat_list_skeleton"
        :class="{
            'workspace_chat_list_skeleton--collapsed': collapsed,
            'workspace_chat_list_skeleton--compact': compact
        }"
        role="status"
        :aria-label="label"
    >
        <span class="chat_skeleton_sr_only">
            {{ label }}
        </span>

        <!--
            These two wrapper classes intentionally match the real search
            field. Loading completion therefore does not change its geometry,
            border colour, radius, or vertical position.
        -->
        <div
            class="
                user_search_for_new_chat
                chat_search_skeleton
            "
            aria-hidden="true"
        >
            <div
                class="
                    search_for_new_chat_inner
                    chat_search_skeleton_inner
                "
            >
                <span
                    class="
                        chat_skeleton_block
                        chat_search_skeleton_icon
                    "
                />

                <span
                    class="
                        chat_skeleton_block
                        chat_search_skeleton_input
                    "
                />
            </div>
        </div>

        <div
            class="chat_list_loading_shell"
            aria-hidden="true"
        >
            <div class="chat_list_skeleton">
                <div
                    v-for="index in rowCount"
                    :key="'chat-list-loading-' + index"
                    class="chat_list_skeleton_item"
                >
                    <span
                        class="
                            chat_skeleton_block
                            chat_list_skeleton_avatar
                        "
                    />

                    <span class="chat_list_skeleton_content">
                        <span
                            class="
                                chat_skeleton_block
                                chat_list_skeleton_name
                            "
                            :class="
                                'chat_list_skeleton_name--' +
                                (((index - 1) % 3) + 1)
                            "
                        />

                        <!-- <span
                            class="
                                chat_skeleton_block
                                chat_list_skeleton_preview
                            "
                        /> -->
                    </span>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'ChatListSkeleton',

    props: {
        collapsed: {
            type: Boolean,
            default: false
        },

        compact: {
            type: Boolean,
            default: false
        },

        rowCount: {
            type: Number,
            default: 6,

            validator(value) {
                return (
                    Number.isInteger(value) &&
                    value > 0 &&
                    value <= 20
                );
            }
        },

        label: {
            type: String,
            default: 'Loading conversations'
        }
    }
};
</script>

<style lang="scss" scoped>
.workspace_chat_list_skeleton {
    display: flex;
    width: 100%;
    min-width: 0;
    min-height: 0;
    overflow: hidden;
    flex: 1 1 auto;
    flex-direction: column;

    &--collapsed {
        .user_search_for_new_chat {
            max-height: 0;
            padding-top: 0;
            padding-bottom: 0;
            pointer-events: none;
            opacity: 0;
            border-bottom-color: transparent;
        }
    }

    &--compact {
        .user_search_for_new_chat {
            visibility: hidden;
        }

        .chat_list_skeleton_item {
            padding-right: 0;
            padding-left: 0;
            justify-content: center;
        }

        .chat_list_skeleton_content {
            display: none;
        }
    }
}

.chat_skeleton_sr_only {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
}

.chat_skeleton_block {
    display: block;
    background-image: linear-gradient(
        90deg,
        #20262b 20%,
        #343d44 38%,
        #20262b 62%
    );
    background-position: 100% 0;
    background-size: 400% 100%;
    animation:
        chat-list-skeleton-shimmer
        1.35s ease-in-out infinite;
}

.user_search_for_new_chat {
    max-height: 80px;
    padding: 10px;
    overflow: hidden;
    opacity: 1;
    border-bottom:
        1px solid rgba(255, 255, 255, 0.04);
    background: #111111;
    flex: 0 0 auto;
    transition:
        max-height 220ms cubic-bezier(0.4, 0, 0.2, 1),
        padding-top 220ms cubic-bezier(0.4, 0, 0.2, 1),
        padding-bottom 220ms cubic-bezier(0.4, 0, 0.2, 1),
        opacity 140ms ease,
        border-color 140ms ease;
}

.search_for_new_chat_inner {
    position: relative;
    display: flex;
    width: 100%;
    min-height: 44px;
    overflow: hidden;
    align-items: center;
    border:
        2px solid rgba(255, 255, 255, 0.15);
    border-radius: 24px;
    background: rgba(255, 255, 255, 0.03);
}

.chat_search_skeleton_icon {
    position: absolute;
    top: 50%;
    left: 13px;
    z-index: 1;
    width: 17px;
    height: 17px;
    border-radius: 50%;
    transform: translateY(-50%);
}

.chat_search_skeleton_input {
    width: 42%;
    max-width: 145px;
    height: 10px;
    margin-left: 40px;
    border-radius: 5px;
}

.chat_list_loading_shell {
    display: flex;
    min-height: 0;
    overflow: hidden;
    flex: 1 1 auto;
    flex-direction: column;
}

.chat_list_skeleton {
    min-height: 0;
    overflow: hidden;
    flex: 1 1 auto;
}

.chat_list_skeleton_item {
    display: flex;
    min-height: 62px;
    padding:8px 10px;
    align-items: center;
}

.chat_list_skeleton_avatar {
    width: 46px;
    height: 46px;
    border-radius: 50%;
    flex: 0 0 46px;
}

.chat_list_skeleton_content {
    display: block;
    min-width: 0;
    padding-left: 13px;
    flex: 1 1 auto;
}

.chat_list_skeleton_name,
.chat_list_skeleton_preview {
    border-radius: 5px;
}

.chat_list_skeleton_name {
    width: 55%;
    height: 13px;
    margin-bottom: 0px;

    &--2 {
        width: 72%;
    }

    &--3 {
        width: 48%;
    }
}

.chat_list_skeleton_preview {
    width: 78%;
    height: 9px;
    opacity: 0.72;
}

@keyframes chat-list-skeleton-shimmer {
    from {
        background-position: 100% 0;
    }

    to {
        background-position: -100% 0;
    }
}

@media (prefers-reduced-motion: reduce) {
    .chat_skeleton_block {
        animation: none;
    }

    .user_search_for_new_chat {
        transition: none;
    }
}
</style>
